// Cognito Hosted UI federated sign-in (Authorization Code + PKCE), matching
// how the mobile app already signs in with Google - Cognito talks to Google
// as an Identity Provider itself, so the token we get back is Cognito-issued
// and works with the backend's existing auth guard unchanged.

const COGNITO_DOMAIN =
  process.env.REACT_APP_CUSTOM_ENV === "dev"
    ? process.env.REACT_APP_COGNITO_DOMAIN_DEV
    : process.env.REACT_APP_COGNITO_DOMAIN_PROD;

const COGNITO_CLIENT_ID =
  process.env.REACT_APP_CUSTOM_ENV === "dev"
    ? process.env.REACT_APP_COGNITO_CLIENT_ID_DEV
    : process.env.REACT_APP_COGNITO_CLIENT_ID_PROD;

const CALLBACK_PATH = "/auth/callback";
const VERIFIER_STORAGE_KEY = "uvita-oauth-code-verifier";
const STATE_STORAGE_KEY = "uvita-oauth-state";
const START_GOOGLE_PARAM = "startGoogle";

function base64UrlEncode(bytes) {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomString(length) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256(value) {
  const data = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

export function getRedirectUri() {
  return `${window.location.origin}${CALLBACK_PATH}`;
}

// Cognito's Hosted UI sets its own "cognito" SSO cookie (valid up to 1 hour)
// on the Cognito domain, separate from Google's session. Clearing local app
// state alone doesn't touch it, so a later /oauth2/authorize call can skip
// Google entirely and silently re-auth the same account - which is also why
// prompt=select_account below doesn't help on its own.
// Note: Cognito's /logout only clears *its own* session, not Google's - see
// https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
export function redirectToCognitoLogout() {
  if (!COGNITO_DOMAIN || !COGNITO_CLIENT_ID) {
    return false;
  }

  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    logout_uri: `${window.location.origin}/Login`,
  });

  window.location.href = `https://${COGNITO_DOMAIN}/logout?${params.toString()}`;
  return true;
}

async function beginGoogleAuthorize() {
  const codeVerifier = randomString(64);
  const state = randomString(32);
  const codeChallenge = await sha256(codeVerifier);

  sessionStorage.setItem(VERIFIER_STORAGE_KEY, codeVerifier);
  sessionStorage.setItem(STATE_STORAGE_KEY, state);

  const params = new URLSearchParams({
    identity_provider: "Google",
    response_type: "code",
    client_id: COGNITO_CLIENT_ID,
    redirect_uri: getRedirectUri(),
    scope: "openid email",
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    // Force Google's account chooser once we actually reach Google - only
    // effective once Cognito's own session is cleared first (below), since
    // Cognito can otherwise skip Google entirely.
    prompt: "select_account",
  });

  window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

// Entry point for every "Sign in/up with Google" button. Always bounces
// through Cognito's own /logout first to clear its SSO cookie, then lands
// back here (LoginPage/RegisterPage call resumeGoogleSignInIfRequested() on
// mount) to continue to the real Google authorize redirect - this is AWS's
// documented workaround for forcing re-auth with a federated IdP, since
// Cognito's session alone can bypass Google regardless of any prompt value:
// https://repost.aws/questions/QUeiGRg14VSxmHeyLlWtS_7Q
export async function redirectToGoogleSignIn() {
  if (!COGNITO_DOMAIN || !COGNITO_CLIENT_ID) {
    throw new Error(
      "Cognito Hosted UI is not configured for this environment."
    );
  }

  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    // Always the same fixed URL regardless of which page (Login or
    // Register) started this, since Cognito matches logout_uri by exact
    // string against the Allowed sign-out URLs list - one registered URL
    // to maintain instead of one per page. LoginPage's resume check below
    // picks the flow back up and continues on to Google from there even if
    // Register initiated it; the user only sees a brief redirect flash.
    logout_uri: `${window.location.origin}/Login?${START_GOOGLE_PARAM}=1`,
  });

  window.location.href = `https://${COGNITO_DOMAIN}/logout?${params.toString()}`;
}

// Call on mount from any page with a "Sign in/up with Google" button.
// Returns true (and continues on to Google) if we just landed back from the
// /logout bounce above; false otherwise, so the caller can render its
// normal form.
export function resumeGoogleSignInIfRequested() {
  const params = new URLSearchParams(window.location.search);
  if (params.get(START_GOOGLE_PARAM) !== "1") {
    return false;
  }
  window.history.replaceState(null, "", window.location.pathname);
  beginGoogleAuthorize();
  return true;
}

// Exchanges the authorization code Cognito redirected back with for real
// Cognito tokens. Talks directly to Cognito's OAuth token endpoint - not a
// GraphQL call, so it bypasses Apollo entirely.
export async function exchangeCodeForTokens(code, state) {
  const expectedState = sessionStorage.getItem(STATE_STORAGE_KEY);
  const codeVerifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
  sessionStorage.removeItem(STATE_STORAGE_KEY);
  sessionStorage.removeItem(VERIFIER_STORAGE_KEY);

  if (!state || state !== expectedState) {
    throw new Error("Invalid sign-in state. Please try again.");
  }
  if (!codeVerifier) {
    throw new Error("Missing sign-in session. Please try again.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: COGNITO_CLIENT_ID,
    code,
    redirect_uri: getRedirectUri(),
    code_verifier: codeVerifier,
  });

  const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || "Google sign-in failed. Please try again.");
  }

  const tokens = await response.json();
  return {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
  };
}
